import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { quotes } from 'html2canvas/dist/types/css/property-descriptors/quotes';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { LoaderService } from 'src/app/core/services/loader.service';
import {
  ConcatenatedHour,
  Hour,
  HoursAvailabilityResponse,
} from 'src/app/pages/new-service/interfaces/Hour';

import { LocationInfo, SummaryData } from 'src/app/pages/new-service/interfaces/SummaryData';
import { FunnelService } from 'src/app/pages/new-service/services/funnel.service';
import { ES_Zones } from 'src/app/core/constants/ZonesIds';

@Injectable({
  providedIn: 'root',
})
export class SharedFunnelService {
  constructor(
    private readonly funnelService: FunnelService,
    private readonly loader: LoaderService,
    private readonly translate: TranslateService
  ) { }



  getProductAvailabilityShared(
    productId: string,
    selectedDate: Date,
    isSpecificHours: boolean,
    isReturn = false,
    // summaryData?: SummaryData
  ): Observable<Hour[]> {
    this.loader.show();
    return new Observable((observer) => {
      this.funnelService
        .getProductAvailabilityRequest({
          productId,
          selectedDate: this.formatDateTime(selectedDate),
          isSpecificHours: isReturn ? isSpecificHours : isSpecificHours,
        })
        .subscribe({
          next: (response: Hour[]) => {
            // let hours = response
            //   .filter((r: Hour) => !r.Disabled)
            //   .map((r: HoursAvailabilityResponse) => {
            //     return { Text: r.Text, Key: r.Key, Fee: r.Fee };
            //   });
            // if (summaryData) {
            //   const destinationServiceDate =
            //     summaryData.locationInfo?.serviceEndDate?.split('T')[0];
            //   const selectedDateString = this.formatDateTime(selectedDate).split('T')[0];

            //   if (selectedDateString === destinationServiceDate) {
            //     const [destinationHour, destinationMinute] = (
            //       summaryData.locationInfo?.formattedStartTime || ''
            //     ).split(':');
            //     const destinationEndTime =
            //       parseInt(destinationHour) * 60 + parseInt(destinationMinute);

            //     hours = hours.filter((hour: any) => {
            //       const [hourStr, minuteStr] = hour.Text.split(':');
            //       const hourTime = parseInt(hourStr) * 60 + parseInt(minuteStr);
            //       return hourTime >= destinationEndTime;
            //     });
            //   }
            // }
            observer.next(response);
            observer.complete();
          },
          error: (error) => {
            console.error('Error loading product availability', error);
            observer.error(error);
            this.loader.hide();
          },
          complete: () => {
            this.loader.hide();
          },
        });
    });
  }

  formatHour_to_HoursAvailabilityResponse(hours: Hour[]): HoursAvailabilityResponse[] {
    return [...hours].map((r: HoursAvailabilityResponse) => {
      return { Text: r.Text, Key: r.Key, Fee: r.Fee };
    })
  }

  filterDisabledHours(hours: Hour[]): Hour[] {
    return [...hours].filter((r: Hour) => !r.Disabled)
  }

  formatDateTime(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  transformHours(hours: Hour[]): ConcatenatedHour[] {
    const concatenatedHours: ConcatenatedHour[] = [];
    for (let i = 0; i < hours.length; i += 1) {
      if (i + 1 < hours.length) {
        const StartHour = hours[i]
        let EndHour = hours[i + 1];
        const is30minRange = this.check30minRange(hours[i], hours[i + 1])
        if (!is30minRange) {
          EndHour = this.setEndHourByStartHour(StartHour)
        }
        concatenatedHours.push({
          start: StartHour,
          end: EndHour,
          displayText: `${StartHour.Text} - ${EndHour.Text}`,
          disabled: StartHour.Disabled,
          quota: StartHour.Quota
        });
      }
    }
    return concatenatedHours;
  }

  check30minRange(start: Hour, end: Hour): boolean {
    const THIRTY_MINUTES = 30;

    const startIso = start.Key.split(" ")[0];
    const endIso = end.Key.split(" ")[0];

    const startMoment = moment(startIso);
    const endMoment = moment(endIso);

    const diff = endMoment.diff(startMoment, "minutes");
    return diff <= THIRTY_MINUTES;
  }

  setEndHourByStartHour(startHour: Hour): Hour {
    const [startIso, timeZone] = startHour.Key.split(" ");


    const endIso = moment(startIso).add(30, "minutes")

    const endHour: Hour = {
      ...startHour,
      Key: `${endIso.format("YYYY-MM-DDTHH:mm:ssZ")} ${timeZone}`,
      Text: endIso.format("HH:mm")
    }
    return endHour
  }



  handleStartTimeChange(
    event: any,
    availableHours: HoursAvailabilityResponse[],
    summaryData: SummaryData,
    withoutEndless: boolean = false
  ): {
    availableEndHours: HoursAvailabilityResponse[];
    noAvailableEndHours: boolean;
    selectedEndHour: string;
    warningMessage: string;
  } {
    const startIndex = availableHours.findIndex((hour) => hour?.Text === event?.Text);

    if (startIndex !== -1) {
      const startTime = new Date(`2000-01-01T${event.Text}`);
      const endTime = new Date(startTime.getTime() + 2 * 60 * 60 * 1000);
      const latestEndTime = new Date(`2000-01-01T${availableHours[availableHours.length - 1].Text}`);

      // Spain allows a 2h gap (end can be exactly start + 2h); other countries keep the 3h gap.
      const zoneId = summaryData?.productInformation?.ZoneId;
      const isSpain = !!zoneId && ES_Zones.includes(zoneId);

      const availableEndHours = availableHours.filter((hour) => {
        const hourTime = new Date(`2000-01-01T${hour.Text}`);
        const meetsMinGap = isSpain ? hourTime >= endTime : hourTime > endTime;
        return meetsMinGap && (hourTime <= latestEndTime || withoutEndless);
      });

      if (availableEndHours.length === 0) {
        if (summaryData.locationInfo?.formattedEndTime) {
          summaryData.locationInfo.formattedEndTime = '';
        }
        return {
          availableEndHours,
          noAvailableEndHours: true,
          selectedEndHour: '',
          warningMessage: this.translate.instant('changePickupTimeOrContactSupport'),
        };
      } else {
        return {
          availableEndHours,
          noAvailableEndHours: false,
          selectedEndHour: availableEndHours[0].Text || '',
          warningMessage: '',
        };
      }
    } else {
      return {
        availableEndHours: [],
        noAvailableEndHours: true,
        selectedEndHour: '',
        warningMessage: '',
      };
    }
  }

  checkDateTime(selectedDate: Date, currentDate: Date = new Date()): boolean {
    currentDate.setHours(0, 0, 0);
    const warningEnd = new Date();
    warningEnd.setDate(currentDate.getDate() + 1);
    warningEnd.setHours(23, 59, 59);

    const dayOfWeekToday = currentDate.getDay();
    const dayOfWeekSelected = selectedDate.getDay();

    const isTodayFriday = dayOfWeekToday === 5;
    const isSelectedDateMonday = dayOfWeekSelected === 1;

    return (
      (selectedDate >= currentDate && selectedDate <= warningEnd) ||
      (isTodayFriday && isSelectedDateMonday)
    );
  }

  handleTimeSlotChange(event: ConcatenatedHour | null): LocationInfo {

    if (!event) {
      return {
        serviceStartDate: "",
        serviceEndDate: "",
        formattedStartDate: "",
        formattedStartTime: "",
        formattedEndTime: "",
        // fee: 0,
      };
    }

    const rawDate = event.start.Key;
    const [datetimePart, tzPart] = rawDate.split(' ');
    const date = moment.tz(datetimePart, tzPart);
    const formatted = date.format('DD/MM/YYYY');

    return {
      serviceStartDate: event.start.Key,
      serviceEndDate: event.end.Key,
      formattedStartDate: formatted,
      formattedStartTime: event.start.Text,
      formattedEndTime: event.end.Text,
      fee: event.start.Fee,
    };
  }

  resetTimeSlotSelection(): {
    selectedStartHour: null;
    selectedEndHour: string;
    availableStartHours: HoursAvailabilityResponse[];
    availableEndHours: HoursAvailabilityResponse[];
    selectedHourPair: null;
    noAvailableHours: boolean;
    showDropdown: boolean;
  } {
    return {
      selectedStartHour: null,
      selectedEndHour: '',
      availableStartHours: [],
      availableEndHours: [],
      selectedHourPair: null,
      noAvailableHours: false,
      showDropdown: false,
    };
  }

  // Añade aquí cualquier otro método común que encuentres en ambos componentes
}
